/* ============================================================================
   MTL Silent Personalization Pipeline — Frontend
   v3 — 2026-04-20: AbortController timeout, Horizon swatch-button click hook,
                    URL-fallback variant detection, inFlight watchdog.
   ----------------------------------------------------------------------------
   Listens to the branded name input → debounces 500ms → calls Cloudflare
   Worker → writes properties[Personalization ID] + properties[_Personalization]
   to cart form → swaps preview image → gates ATC.

   Coexists with the existing mtl-personalization.js (layout/swatches/etc).
   Self-contained. Load after DOMContentLoaded.

   Worker: https://mtl-personalize.steep-lab-a49a.workers.dev
   ============================================================================ */
(function () {
  'use strict';

  const WORKER_URL = 'https://mtl-personalize.steep-lab-a49a.workers.dev/personalize';
  const DEBOUNCE_MS = 500;
  const FETCH_TIMEOUT_MS = 9000;       // hard cap on Worker call before abort + error
  const INFLIGHT_WATCHDOG_MS = 11000;   // safety net: force-clear inFlight if anything wedges

  // Shopify variant → everything we need to generate the correct sharedId and
  // render the correct UX for that variant. Add an entry per variant we roll
  // the silent pipeline out to.
  //   fulfillerVariant  : the fulfiller's internal variant id (used by the personalization API)
  //   templateProductId: the fulfiller's product id (whitelisted on the Worker)
  //   swatchKey        : STABLE machine token from the swatch's data-mtl-swatch
  //                      attribute. THIS is what selects the variant. It is not
  //                      customer-facing and did not change in the FTC relabel.
  //   label            : human finish name as it appears INSIDE the swatch text.
  //                      Fallback matching only — never the primary source.
  //   variantTitle     : the live Shopify variant title, verified 2026-08-07.
  //                      Recorded so label/data drift is visible on inspection
  //                      and assertable by the nightly guard. Not used at runtime.
  //   maxLength        : character limit enforced by the Worker for this product
  //   displayPrice     : LEGACY. Unreachable since the 2026-08-07 price fix
  //                      (form.dataset.price wins). Stale June-2026 figures.
  //                      Scheduled for deletion — do not reuse.
  const VARIANT_MAP = {
    // Always With Me Heart Necklace
    47253644837013: { fulfillerVariant: 147718, templateProductId: '69dcf0ec94a2a2acde057407', swatchKey: 'gold',     label: 'Gold',            variantTitle: 'Gold – 18K PVD',                        maxLength: 10, displayPrice: '$47.99' },
    47253644869781: { fulfillerVariant: 147721, templateProductId: '69dcf0ec94a2a2acde057407', swatchKey: 'silver',   label: 'Stainless Steel', variantTitle: 'Stainless Steel - 316L Surgical Grade', maxLength: 10, displayPrice: '$47.99' },
    // Paw Bar Name Necklace ("MAGGIE")
    47254673424533: { fulfillerVariant: 147706, templateProductId: '69dd0f9fffc76ae14901f12b', swatchKey: 'gold',     label: 'Gold',            variantTitle: 'Gold – 18K PVD',                        maxLength: 15, displayPrice: '$44.99' },
    47254673457301: { fulfillerVariant: 147709, templateProductId: '69dd0f9fffc76ae14901f12b', swatchKey: 'silver',   label: 'Stainless Steel', variantTitle: 'Stainless Steel - 316L Surgical Grade', maxLength: 15, displayPrice: '$44.99' },
    // Pet Name Heart Necklace ("COOPER")
    47254656680085: { fulfillerVariant: 147718, templateProductId: '69dd0c660830a44cfa0e4826', swatchKey: 'gold',     label: 'Gold',            variantTitle: 'Gold – 18K PVD',                        maxLength: 10, displayPrice: '$47.99' },
    47254656712853: { fulfillerVariant: 147721, templateProductId: '69dd0c660830a44cfa0e4826', swatchKey: 'silver',   label: 'Stainless Steel', variantTitle: 'Stainless Steel - 316L Surgical Grade', maxLength: 10, displayPrice: '$47.99' },
    // Sliding Bar Cat Paw — single variant (Stainless outside + Gold inside)
    47254716547221: { fulfillerVariant: 164458, templateProductId: '69dd16af0830a44cfa0e49a9', swatchKey: 'silver',   label: 'Stainless Steel', variantTitle: 'Stainless Steel Outside, Gold Inside',   maxLength: 10, displayPrice: '$59.00' },
    // Sliding Bar Dog Paw — single variant (Stainless outside + Gold inside)
    47252905230485: { fulfillerVariant: 164458, templateProductId: '69dcc04be8f2cd193a0ee0d5', swatchKey: 'silver',   label: 'Stainless Steel', variantTitle: 'Stainless Steel Outside, Gold Inside',   maxLength: 10, displayPrice: '$59.00' },
    // Heart Cutout Necklace ("Molly") — char limit 12; Rose Gold added (2026-07-06)
    47279575367829: { fulfillerVariant: 147729, templateProductId: '69e8c0ad7027eebee60950a0', swatchKey: 'gold',     label: 'Gold',            variantTitle: 'Gold – 18K PVD',                        maxLength: 12, displayPrice: '$42.00' },
    47279575400597: { fulfillerVariant: 147731, templateProductId: '69e8c0ad7027eebee60950a0', swatchKey: 'silver',   label: 'Stainless Steel', variantTitle: 'Stainless Steel - 316L Surgical Grade', maxLength: 12, displayPrice: '$42.00' },
    47395385180309: { fulfillerVariant: 147730, templateProductId: '69e8c0ad7027eebee60950a0', swatchKey: 'rosegold', label: 'Rose Gold',       variantTitle: 'Rose Gold – 18K PVD',                   maxLength: 12, displayPrice: '$42.00' },
    // Paperclip Heart Necklace
    47280265691285: { fulfillerVariant: 164438, templateProductId: '69e8fcc8a1496c73d004ea29', swatchKey: 'gold',     label: 'Gold',            variantTitle: 'Gold – 18K PVD',                        maxLength: 10, displayPrice: '$49.00' },
    47280265724053: { fulfillerVariant: 164440, templateProductId: '69e8fcc8a1496c73d004ea29', swatchKey: 'silver',   label: 'Stainless Steel', variantTitle: 'Stainless Steel - 316L Surgical Grade', maxLength: 10, displayPrice: '$49.00' },
    // Bella Forever Break-Apart Heart ("Charlie")
    47279766339733: { fulfillerVariant: 164443, templateProductId: '69e8c92c7027eebee609546e', swatchKey: 'gold',     label: 'Gold',            variantTitle: 'Gold – 18K PVD',                        maxLength: 10, displayPrice: '$49.00' },
    47279766372501: { fulfillerVariant: 164444, templateProductId: '69e8c92c7027eebee609546e', swatchKey: 'silver',   label: 'Stainless Steel', variantTitle: 'Stainless Steel - 316L Surgical Grade', maxLength: 10, displayPrice: '$49.00' },
    // Soul Dog Circle Necklace ("Toby")
    47280667164821: { fulfillerVariant: 147652, templateProductId: '69e92e4a9269eec08009422d', swatchKey: 'gold',     label: 'Gold',            variantTitle: 'Gold – 18K PVD',                        maxLength: 10, displayPrice: '$44.00' },
    47280667197589: { fulfillerVariant: 147655, templateProductId: '69e92e4a9269eec08009422d', swatchKey: 'silver',   label: 'Stainless Steel', variantTitle: 'Stainless Steel - 316L Surgical Grade', maxLength: 10, displayPrice: '$44.00' },
    // Guardian Angel Necklace (halo + angel-wing heart, "Forever Loved", human memorial) — added 2026-10-17
    67788190122133: { fulfillerVariant: 147718, templateProductId: '6ad222d88fb36041150325ae', swatchKey: 'gold',     label: 'Gold',            variantTitle: 'Gold – 18K PVD',                        maxLength: 10, displayPrice: '$39.00' },
    67788190154901: { fulfillerVariant: 147721, templateProductId: '6ad222d88fb36041150325ae', swatchKey: 'silver',   label: 'Stainless Steel', variantTitle: 'Stainless Steel - 316L Surgical Grade', maxLength: 10, displayPrice: '$39.00' },
    67900505424021: { fulfillerVariant: 147718, templateProductId: '6ad4ded255fae0bc720d4608', swatchKey: 'gold',     label: 'Gold',            variantTitle: 'Gold – 18K PVD',                         maxLength: 10, displayPrice: '$39.00' },  // mom memorial (2026-10-18)
    67900505456789: { fulfillerVariant: 147721, templateProductId: '6ad4ded255fae0bc720d4608', swatchKey: 'silver',   label: 'Stainless Steel', variantTitle: 'Stainless Steel - 316L Surgical Grade', maxLength: 10, displayPrice: '$39.00' },  // mom memorial (2026-10-18)
    67900551725205: { fulfillerVariant: 147718, templateProductId: '6ad4dedf7e86416ffc0961e4', swatchKey: 'gold',     label: 'Gold',            variantTitle: 'Gold – 18K PVD',                         maxLength: 10, displayPrice: '$39.00' },  // grandma memorial (2026-10-18)
    67900551757973: { fulfillerVariant: 147721, templateProductId: '6ad4dedf7e86416ffc0961e4', swatchKey: 'silver',   label: 'Stainless Steel', variantTitle: 'Stainless Steel - 316L Surgical Grade', maxLength: 10, displayPrice: '$39.00' },  // grandma memorial (2026-10-18)
    67900558049429: { fulfillerVariant: 147718, templateProductId: '6ad4deecc5bf429b0a0817a7', swatchKey: 'gold',     label: 'Gold',            variantTitle: 'Gold – 18K PVD',                         maxLength: 10, displayPrice: '$39.00' },  // baby memorial (2026-10-18)
    67900558082197: { fulfillerVariant: 147721, templateProductId: '6ad4deecc5bf429b0a0817a7', swatchKey: 'silver',   label: 'Stainless Steel', variantTitle: 'Stainless Steel - 316L Surgical Grade', maxLength: 10, displayPrice: '$39.00' },  // baby memorial (2026-10-18)
    // Forever Loved Angel Wing Pet Necklace (paw + angel wings heart, "Forever Loved", pet memorial) — added 2026-10-17
    67786011541653: { fulfillerVariant: 147718, templateProductId: '6ad2180920886a0bc203d1f2', swatchKey: 'gold', label: 'Gold', variantTitle: 'Gold – 18K PVD', maxLength: 10, displayPrice: '$39.00' },
    67786011574421: { fulfillerVariant: 147721, templateProductId: '6ad2180920886a0bc203d1f2', swatchKey: 'silver', label: 'Stainless Steel', variantTitle: 'Stainless Steel - 316L Surgical Grade', maxLength: 10, displayPrice: '$39.00' },
  };
  // Which upstream product this page is for — set at init() from the initial
  // variant. Used so a swatch-label lookup ("Silver") knows which of the
  // several Silver variants in VARIANT_MAP belongs to THIS page.
  let pageProductTemplateId = null;

  const TAG = '[MTL-Silent]';
  function log() { try { console.log.apply(console, [TAG, ...arguments]); } catch (_) {} }
  function warn() { try { console.warn.apply(console, [TAG, ...arguments]); } catch (_) {} }
  function err() { try { console.error.apply(console, [TAG, ...arguments]); } catch (_) {} }

  // State
  let debounceTimer = null;
  let latestTypedAt = 0;
  let latestSharedIdAt = 0;
  let currentSharedId = null;
  let lastProcessedText = null;
  let inFlight = false;
  let inFlightWatchdog = null;
  let currentAbortController = null;

  function setInFlight(val) {
    inFlight = !!val;
    clearTimeout(inFlightWatchdog);
    if (inFlight) {
      inFlightWatchdog = setTimeout(() => {
        if (inFlight) {
          warn('inFlight watchdog fired — force-clearing stuck state');
          try { currentAbortController && currentAbortController.abort(); } catch (_) {}
          inFlight = false;
          showError('Took too long — tap below to try again.');
          gateAtc('locked');
        }
      }, INFLIGHT_WATCHDOG_MS);
    }
  }

  function getForm() {
    return document.querySelector('form[action*="/cart/add"]');
  }

  function getNameInput() {
    return document.querySelector('[data-mtl="name-input"]');
  }

  function getAtcBtn() {
    return document.querySelector('[data-mtl="atc-btn"]');
  }

  // True when a finish swatch IS selected but we could not map it to a variant.
  // While this is true we refuse to add to cart. Guessing here is exactly what
  // caused a finish mismatch between the selected swatch and the mapped variant.
  let unresolvedSwatch = false;

  function getActiveSwatch() {
    // Prefer OUR section's finish swatches so a Horizon picker elsewhere on the
    // page (recommendations, quick-add) can never be mistaken for the finish.
    const scoped = document.querySelector(
      'form.mtl-p .step-finish .swatches button.swatch.active, ' +
      'form.mtl-p .step-finish .swatches button.swatch[aria-pressed="true"], ' +
      'form.mtl-p .step-finish .swatches button.swatch[aria-checked="true"]'
    );
    if (scoped) return scoped;
    return document.querySelector(
      'button.swatch.active, button.swatch[aria-pressed="true"], button.swatch[aria-checked="true"]'
    );
  }

  // Resolve an active swatch element to a Shopify variant id.
  // Order is deliberate: most stable source first, rendered text LAST.
  // Labels have already been renamed once (FTC relabel) and will be again.
  function resolveSwatchVariant(active) {
    // TIER A — the swatch carries the real Shopify variant id. Nothing to map.
    const dv = active.getAttribute('data-variant-id');
    if (dv && /^\d+$/.test(dv)) return { vid: parseInt(dv, 10), via: 'data-variant-id' };

    // TIER B — stable machine token, scoped to THIS page's the fulfiller product.
    // data-mtl-swatch is 'gold' | 'silver' | 'rosegold' and is not customer-facing.
    const key = active.getAttribute('data-mtl-swatch');
    if (key && pageProductTemplateId) {
      for (const vid in VARIANT_MAP) {
        const e = VARIANT_MAP[vid];
        if (e.templateProductId === pageProductTemplateId && e.swatchKey === key) {
          return { vid: parseInt(vid, 10), via: 'data-mtl-swatch=' + key };
        }
      }
    }

    // TIER C — legacy rendered-text match, LONGEST label wins so that
    // "Rose Gold18K PVD" cannot be swallowed by the shorter "Gold" entry.
    // (Ported from mtl-silent-pipeline.js, fix 2026-05-30.)
    if (pageProductTemplateId) {
      const txt = (active.textContent || '').toLowerCase();
      let bestVid = null;
      let bestLen = 0;
      for (const vid in VARIANT_MAP) {
        const e = VARIANT_MAP[vid];
        if (e.templateProductId === pageProductTemplateId &&
            txt.indexOf(e.label.toLowerCase()) > -1 &&
            e.label.length > bestLen) {
          bestLen = e.label.length;
          bestVid = parseInt(vid, 10);
        }
      }
      if (bestVid !== null) return { vid: bestVid, via: 'label-text(longest)' };
    }
    return null;
  }

  function getCurrentShopifyVariantId() {
    // If a finish swatch is active it is the ONLY authority. Horizon does not
    // write ?variant= on these pages and does not sync form input[name="id"]
    // until its own submit handler, so both fallbacks below return the page
    // default (Gold) regardless of what the customer clicked. Verified live
    // 2026-08-07 — the old comment claiming Horizon rewrites the input at
    // submit time was wrong.
    let active = null;
    try { active = getActiveSwatch(); } catch (_) {}
    if (active) {
      let r = null;
      try { r = resolveSwatchVariant(active); } catch (_) {}
      if (r) {
        unresolvedSwatch = false;
        return r.vid;
      }
      // FAIL LOUD. Do not fall through to URL/form — they hold the page default
      // and returning it silently mis-ships the order at the correct price.
      unresolvedSwatch = true;
      err('UNRESOLVED FINISH SWATCH — refusing to guess a variant.',
          'data-mtl-swatch=', active.getAttribute('data-mtl-swatch'),
          'text=', JSON.stringify((active.textContent || '').trim()),
          'template=', pageProductTemplateId);
      return null;
    }
    unresolvedSwatch = false;
    try {
      const m = window.location.search.match(/[?&]variant=(\d+)/);
      if (m) return parseInt(m[1], 10);
    } catch (_) {}
    const form = getForm();
    if (!form) return null;
    const idInput = form.querySelector('input[name="id"]');
    return idInput ? parseInt(idInput.value, 10) : null;
  }
  function getPreviewImg() {
    // Horizon theme + our custom section
    return document.querySelector(
      '.product__media img, .product-image img, [data-mtl="preview-image"], .product-gallery img, .product__primary-image'
    );
  }

  function setHidden(name, value) {
    const form = getForm();
    if (!form) return;
    let input = form.querySelector(`input[name="${name}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }

  function clearHidden(name) {
    const form = getForm();
    if (!form) return;
    form.querySelectorAll(`input[name="${name}"]`).forEach(el => el.remove());
  }

  function gateAtc(reason) {
    const atc = getAtcBtn();
    if (!atc) return;
    if (reason === 'locked') {
      atc.disabled = true;
      atc.setAttribute('aria-disabled', 'true');
      atc.dataset.mtlGate = 'locked';
    } else if (reason === 'loading') {
      // TIER 1 OPTIMISTIC UX (2026-04-25): keep button visually enabled with the
      // ready-state label while the Worker call runs in background. Internal
      // state still tracks 'loading' via dataset.mtlGate. The form submit
      // handler will hold the click briefly if customer taps Add to Cart
      // before the pipeline finishes.
      atc.disabled = false;
      atc.removeAttribute('aria-disabled');
      atc.dataset.mtlGate = 'loading';
      const text = normalizeText(getNameInput()?.value);
      const variantId = getCurrentShopifyVariantId();
      const price = (getForm() && getForm().dataset.price) || (VARIANT_MAP[variantId] && VARIANT_MAP[variantId].displayPrice) || '';
      if (text) {const __s=document.createElement('span');__s.className='mtl-atc-name';__s.style.setProperty('text-transform','none','important');__s.style.setProperty('pointer-events','none');__s.textContent=text;atc.textContent='Add to Cart — ';atc.appendChild(__s);atc.appendChild(document.createTextNode(' · '+price));} else { atc.textContent = `Add to Cart — ${price}`; }
    } else if (reason === 'ready') {
      atc.disabled = false;
      atc.removeAttribute('aria-disabled');
      atc.dataset.mtlGate = 'ready';
      // Set label including the name if present — show the normalized
      // (all-caps) value so the ATC label matches what gets engraved.
      const text = normalizeText(getNameInput()?.value);
      const variantId = getCurrentShopifyVariantId();
      const price = (getForm() && getForm().dataset.price) || (VARIANT_MAP[variantId] && VARIANT_MAP[variantId].displayPrice) || '';
      if (text) {const __s=document.createElement('span');__s.className='mtl-atc-name';__s.style.setProperty('text-transform','none','important');__s.style.setProperty('pointer-events','none');__s.textContent=text;atc.textContent='Add to Cart — ';atc.appendChild(__s);atc.appendChild(document.createTextNode(' · '+price));} else { atc.textContent = `Add to Cart — ${price}`; }
    }
  }

  function showError(msg) {
    let el = document.querySelector('[data-mtl="silent-error"]');
    if (!el) {
      el = document.createElement('p');
      el.setAttribute('data-mtl', 'silent-error');
      el.setAttribute('role', 'alert');
      el.style.cssText = 'color:#b5362a;font-size:13px;margin:8px 0;padding:10px 14px;background:#fdf0ee;border-radius:8px;border:1px solid #f5c9c0;';
      const input = getNameInput();
      if (input?.parentElement) input.parentElement.appendChild(el);
    }
    el.textContent = msg;
    el.style.display = 'block';
  }

  function clearError() {
    const el = document.querySelector('[data-mtl="silent-error"]');
    if (el) el.style.display = 'none';
  }

  function revealPersonalizerButton() {
    // Failure fallback: show the hidden the fulfiller button so customer can still personalize
    document.querySelectorAll('.mtl-fulfillment-hidden, [data-mtl-fulfillment-hidden]').forEach(el => {
      el.style.setProperty('display', 'block', 'important');
      el.style.removeProperty('position');
      el.style.removeProperty('left');
      el.style.removeProperty('opacity');
    });
  }

  function swapPreview(previewUrl) {
    const img = getPreviewImg();
    if (!img || !previewUrl) return;
    if (!img.dataset.mtlOriginalSrc) img.dataset.mtlOriginalSrc = img.src;
    img.src = previewUrl;
    img.srcset = '';
  }

  // TIER 1 sessionStorage cache (2026-04-25): same (template, variant, text)
  // combo within one browser session reuses the sharedId — no Worker call.
  // Speeds up: customer toggles Gold↔Silver↔Gold, customer re-types same name,
  // customer hits back from cart and re-enters the page. Cache is per-tab/session.
  function cacheKey(templateId, variantId, text) {
    return 'mtl-pid:' + templateId + ':' + variantId + ':' + text;
  }
  function readCache(templateId, variantId, text) {
    try {
      const raw = sessionStorage.getItem(cacheKey(templateId, variantId, text));
      if (!raw) return null;
      const obj = JSON.parse(raw);
      // Sanity: require a personalizationId; ignore stale or malformed entries
      if (!obj || !obj.personalizationId) return null;
      return obj;
    } catch (_) { return null; }
  }
  function writeCache(templateId, variantId, text, payload) {
    try {
      sessionStorage.setItem(cacheKey(templateId, variantId, text), JSON.stringify({
        personalizationId: payload.personalizationId,
        previewUrl: payload.previewUrl || null,
        cachedAt: Date.now(),
      }));
    } catch (_) { /* sessionStorage full or disabled — fail silently, just slower */ }
  }

  async function fetchSharedId(text, shopifyVariantId) {
    const map = VARIANT_MAP[shopifyVariantId];
    if (!map) {
      warn('no variant map for', shopifyVariantId);
      return { fallback: true, reason: 'variant_not_mapped' };
    }
    // TIER 1: cache hit short-circuit (matches Tier 1 doc 2026-04-25)
    const cached = readCache(map.templateProductId, map.fulfillerVariant, text);
    if (cached) {
      log('cache hit — reusing sharedId', cached.personalizationId);
      return { ok: true, personalizationId: cached.personalizationId, previewUrl: cached.previewUrl, _fromCache: true };
    }
    const body = {
      template_product_id: map.templateProductId,
      variant_id: map.fulfillerVariant,
      text,
    };
    currentAbortController = new AbortController();
    const ctrl = currentAbortController;
    const timeoutId = setTimeout(() => { try { ctrl.abort(); } catch (_) {} }, FETCH_TIMEOUT_MS);
    const startedAt = Date.now();
    try {
      const res = await fetch(WORKER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: ctrl.signal,
      });
      clearTimeout(timeoutId);
      const data = await res.json();
      log('worker responded in', Date.now() - startedAt, 'ms');
      if (!res.ok || data.fallback || data.error) {
        return { fallback: true, reason: data.reason || data.error || `http_${res.status}`, data };
      }
      return { ok: true, ...data };
    } catch (err) {
      clearTimeout(timeoutId);
      const reason = err && err.name === 'AbortError' ? 'timeout_or_aborted' : 'network_error';
      warn('worker fetch failed after', Date.now() - startedAt, 'ms —', reason, err);
      return { fallback: true, reason, err: String(err) };
    }
  }

  async function processText(text) {
    if (inFlight) return;
    if (!text || text.length < 1) {
      gateAtc('locked');
      clearHidden('properties[Personalization ID]');
      clearHidden('properties[_Personalization]');
      return;
    }
    // Invalidate any existing sharedId — customer changed text
    currentSharedId = null;
    clearHidden('properties[Personalization ID]');

    const shopifyVariantId = getCurrentShopifyVariantId();
    if (!shopifyVariantId) {
      warn('no variant id, cannot proceed');
      return;
    }

    setInFlight(true);
    gateAtc('loading');
    clearError();

    const result = await fetchSharedId(text, shopifyVariantId);
    setInFlight(false);

    // If text changed while we were fetching, ignore this result.
    // Compare normalized-to-normalized — input.value is the RAW (lowercase)
    // value the user typed; CSS text-transform:uppercase is cosmetic only.
    const currentText = normalizeText(getNameInput()?.value);
    if (currentText !== text) {
      log('stale response, ignoring (current=', currentText, 'sent=', text, ')');
      return;
    }

    if (result.fallback) {
      warn('fallback triggered:', result.reason);
      showError('Personalization temporarily unavailable — tap "Add personalized design" below.');
      revealPersonalizerButton();
      gateAtc('locked');
      return;
    }

    // Success — write both properties, swap preview, unlock ATC
    currentSharedId = result.personalizationId;
    latestSharedIdAt = Date.now();
    lastProcessedText = text;
    setHidden('properties[Personalization ID]', result.personalizationId);
    setHidden('properties[_Personalization]', text);
    // Keep legacy readable props in sync for support
    setHidden('properties[_Engraving]', text);
    if (result.previewUrl) swapPreview(result.previewUrl);
    // TIER 1: write cache (skip if this result already came FROM cache)
    if (!result._fromCache) {
      const map = VARIANT_MAP[shopifyVariantId];
      if (map) writeCache(map.templateProductId, map.fulfillerVariant, text, result);
    }
    gateAtc('ready');
    log('pipeline ready — sharedId:', result.personalizationId, result._fromCache ? '(cache)' : '(worker)');
  }

  function normalizeText(s) {
    // Proper-case variant: preserve the customer's typed case (matches new SKUs like Heart Cutout)
    return (s || '').trim();
  }

  function onInput() {
    const input = getNameInput();
    if (!input) return;
    const text = normalizeText(input.value);
    latestTypedAt = Date.now();
    clearTimeout(debounceTimer);
    gateAtc('loading');
    debounceTimer = setTimeout(() => {
      processText(text);
    }, DEBOUNCE_MS);
  }

  // Track current variant to detect changes
  let currentVariantId = null;

  function onVariantChange() {
    const newVariantId = getCurrentShopifyVariantId();
    if (newVariantId && newVariantId !== currentVariantId) {
      log('variant changed from', currentVariantId, 'to', newVariantId);
      currentVariantId = newVariantId;
      // Invalidate old sharedId — it was for a different variant
      currentSharedId = null;
      lastProcessedText = null;
      clearHidden('properties[Personalization ID]');
      // Abort any in-flight Worker call — its sharedId would be for old variant
      if (currentAbortController) {
        try { currentAbortController.abort(); } catch (_) {}
      }
      setInFlight(false);
      clearError();
      const input = getNameInput();
      const text = normalizeText(input?.value);
      if (text) {
        // Re-kick the pipeline for new variant — short delay (not full debounce)
        clearTimeout(debounceTimer);
        gateAtc('loading');
        debounceTimer = setTimeout(() => processText(text), 80);
      } else {
        gateAtc('locked');
      }
    }
  }

  function init() {
    const input = getNameInput();
    const form = getForm();
    if (!input || !form) {
      log('required elements not found; skipping init');
      return;
    }
    input.addEventListener('input', onInput);
    input.addEventListener('change', onInput);

    // Read the initial variant from URL/form (before any swatch interaction)
    // so we can pin pageProductTemplateId. After this, getCurrentShopifyVariantId
    // will use swatch-aria detection scoped to this product.
    const initialVariantId = (function () {
      try {
        const m = window.location.search.match(/[?&]variant=(\d+)/);
        if (m) return parseInt(m[1], 10);
      } catch (_) {}
      const idInput = form.querySelector('input[name="id"]');
      return idInput ? parseInt(idInput.value, 10) : null;
    })();
    if (initialVariantId && VARIANT_MAP[initialVariantId]) {
      pageProductTemplateId = VARIANT_MAP[initialVariantId].templateProductId;
      // Apply per-product maxLength to the input element so the customer can't
      // type past the engraving limit (Heart=10, Paw Bar=15, etc.).
      const maxLen = VARIANT_MAP[initialVariantId].maxLength;
      if (maxLen) {
        input.setAttribute('maxlength', String(maxLen));
        // Also update the visible counter cap if the section renders one
        const counter = document.querySelector('[data-mtl="char-counter"]');
        if (counter) counter.dataset.max = String(maxLen);
      }
      log('init for product', VARIANT_MAP[initialVariantId].label,
          'template', pageProductTemplateId, 'maxLen', maxLen);

      // DRIFT ASSERTION (2026-08-07): every finish swatch rendered on this page
      // must map to a variant for this product. Catches "a finish was added to
      // the product but not to VARIANT_MAP" — which would otherwise degrade to
      // a silent wrong-variant add-to-cart.
      try {
        const rendered = document.querySelectorAll('form.mtl-p .step-finish .swatches button.swatch');
        rendered.forEach(function (sw) {
          const k = sw.getAttribute('data-mtl-swatch');
          let hit = false;
          for (const vid in VARIANT_MAP) {
            const e = VARIANT_MAP[vid];
            if (e.templateProductId === pageProductTemplateId && e.swatchKey === k) { hit = true; break; }
          }
          if (!hit) {
            err('VARIANT_MAP DRIFT — swatch', JSON.stringify(k),
                'has no entry for template', pageProductTemplateId,
                '- add it before this finish can be sold.');
          }
        });
        log('drift check: %s finish swatches rendered, all mapped', rendered.length);
      } catch (_) {}
    } else {
      log('init: variant not in VARIANT_MAP — silent pipeline will not activate');
      return;
    }

    // Set initial variant
    currentVariantId = getCurrentShopifyVariantId();

    // Watch for variant changes — multiple mechanisms for Shopify theme variety
    const variantInput = form.querySelector('input[name="id"]');
    if (variantInput) {
      // MutationObserver on variant hidden input value attribute
      new MutationObserver(onVariantChange).observe(variantInput, {
        attributes: true,
        attributeFilter: ['value'],
      });
    }
    // Also listen for any change events on the form (swatch clicks bubble up)
    form.addEventListener('change', onVariantChange);

    // Horizon-specific: swatches are <button class="swatch"> — button clicks
    // do NOT fire form `change` and their URL update comes asynchronously.
    // Capture them at document level, then re-check variant multiple times
    // to catch Horizon's async URL/input update.
    document.addEventListener('click', function (ev) {
      const swatch = ev.target && ev.target.closest &&
        ev.target.closest('button.swatch, .swatches button, variant-picker button, [data-variant-id], [data-option-value]');
      if (!swatch) return;
      log('swatch-like button clicked — probing variant');
      // probe at multiple intervals to catch whenever Horizon finishes the swap
      [0, 80, 200, 500, 900].forEach(ms => setTimeout(onVariantChange, ms));
    }, true);

    // Also listen for URL changes (pushState / replaceState / popstate)
    const _push = history.pushState;
    history.pushState = function () { _push.apply(this, arguments); setTimeout(onVariantChange, 0); };
    const _replace = history.replaceState;
    history.replaceState = function () { _replace.apply(this, arguments); setTimeout(onVariantChange, 0); };
    window.addEventListener('popstate', onVariantChange);

    // Poll every 500ms as last-resort failsafe
    setInterval(onVariantChange, 500);

    // Form submit guard — block ATC if no fresh sharedId OR variant drift
    form.addEventListener('submit', function (ev) {
      // HARD GUARD (2026-08-07): a finish is selected but unmappable. Never let
      // this through — the form still holds the page-default variant and the
      // customer would be charged the right price for the wrong finish.
      if (unresolvedSwatch) {
        ev.preventDefault();
        ev.stopPropagation();
        err('blocked add-to-cart: finish swatch could not be resolved to a variant');
        showError('Please re-select your finish and try again.');
        gateAtc('locked');
        return;
      }
      const text = normalizeText(input.value);
      if (!text) return;
      const latestVariant = getCurrentShopifyVariantId();

      // Horizon's swatch picker doesn't sync form input[name="id"] until its
      // own submit handler runs. We run first (capture phase) and force the
      // right variant so the sharedId and the cart line-item agree.
      if (latestVariant) {
        const idInput = form.querySelector('input[name="id"]');
        if (idInput && idInput.value !== String(latestVariant)) {
          log('forcing form variant id to', latestVariant, '(was', idInput.value, ')');
          idInput.value = String(latestVariant);
        }
      }

      const fresh = currentSharedId &&
                    lastProcessedText === text &&
                    latestSharedIdAt >= latestTypedAt &&
                    latestVariant === currentVariantId;
      if (fresh) return;  // Happy path — let submit through

      // TIER 1 GRACEFUL WAIT (2026-04-25): if pipeline is still in flight when
      // customer clicks Add to Cart (because they typed and clicked fast),
      // hold the submit briefly with an inline spinner instead of bouncing
      // them with an error. After it resolves we re-submit the form.
      ev.preventDefault();
      ev.stopPropagation();
      currentVariantId = latestVariant;

      // Show "Saving your design…" briefly inside the ATC button
      const atc = getAtcBtn();
      const originalLabel = atc ? atc.textContent : null;
      if (atc) {
        atc.textContent = 'Saving your design…';
        atc.disabled = true;
      }

      // Kick the pipeline if not already in flight; otherwise let in-flight finish
      if (!inFlight) processText(text);

      // Poll for fresh sharedId — give it up to 8s
      const maxWaitMs = 8000;
      const startWait = Date.now();
      const poller = setInterval(function () {
        const newFresh = currentSharedId &&
                         lastProcessedText === text &&
                         latestSharedIdAt >= latestTypedAt &&
                         latestVariant === currentVariantId;
        if (newFresh) {
          clearInterval(poller);
          if (atc) atc.disabled = false;
          // Re-submit the form. Our handler runs first (capture) and will see
          // fresh=true this time, so it lets the submit pass through to Shopify.
          log('graceful wait succeeded — re-submitting');
          form.submit();
          return;
        }
        if (Date.now() - startWait > maxWaitMs) {
          clearInterval(poller);
          if (atc) {
            atc.disabled = false;
            if (originalLabel) atc.textContent = originalLabel;
          }
          // Fall through to existing error path — customer can retry
          showError('Personalization is taking longer than expected. Tap Add to Cart again to retry.');
        }
      }, 150);
    }, true);

    gateAtc('locked');
    log('silent pipeline initialized; variant=', currentVariantId);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
